import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Tooltip,
    styled,
    useTheme,
} from '@mui/material';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import PauseIcon from '@mui/icons-material/Pause';

import {
    getTooltipTitleForStatus,
    STATUSES_TO_STRING_VALUE,
} from '../utils/constants';

const SIZE_FACTOR = 0.9;

const STATUS_CONFIG = {
    'created': {category: 'new'},

    'incomplete - requester and spender': {category: 'composing', requesterComplete: false, spenderComplete: false},
    'incomplete - spender': {category: 'composing', requesterComplete: true, spenderComplete: false},
    'incomplete - requester': {category: 'composing', requesterComplete: false, spenderComplete: true},
    'saved': {category: 'composing', requesterComplete: true, spenderComplete: true},

    'denied by requester': {category: 'rejected', requesterComplete: false},
    'denied by sb': {category: 'rejected', spenderComplete: false, spenderLabel: 'SB'},
    'denied by ob': {category: 'rejected', spenderComplete: false, spenderLabel: 'OB'},

    'pending requester and spender completion': {category: 'pending', requesterComplete: false, spenderComplete: false},
    'pending spender completion': {category: 'pending', requesterComplete: true, spenderComplete: false},
    'pending requester completion': {category: 'pending', requesterComplete: false, spenderComplete: true},
    'pending requester complete': {category: 'pending', requesterComplete: true, spenderComplete: true},
    'pending sb approval': {category: 'pending', spenderComplete: false, spenderLabel: 'SB'},
    'pending ob approval': {category: 'pending', spenderComplete: false, spenderLabel: 'OB'},

    'submitted': {category: 'submitted'},
};

const CATEGORY_CONFIG = {
    new: {
        label: 'New',
        icon: StarBorderIcon,
        color: '#FFC600',
    },
    composing: {
        label: 'Composing',
        icon: MoreHorizIcon,
        color: '#1061AA',
    },
    pending: {
        label: 'Pending',
        icon: PauseIcon,
        color: '#F59B00',
    },
    rejected: {
        label: 'Rejected',
        icon: ClearIcon,
        color: '#EA4A46',
    },
    submitted: {
        label: 'Submitted',
        icon: CheckCircleIcon,
        color: '#007F0E',
    },
};

const Dot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'fillColor',
})(({fillColor, theme}) => {
    const baseSize = 22;
    const dotSize = baseSize * SIZE_FACTOR;
    return {
        width: dotSize,
        height: dotSize,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${0.75 * SIZE_FACTOR}rem`,
        fontWeight: 700,
        backgroundColor: fillColor,
        color: '#fff',
        border: 'none',
    };
});

const CATEGORY_ICON_SX = {fontSize: 20 * SIZE_FACTOR};

/**
 * @param {StatusBadge.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function StatusBadge({status}) {
    const theme = useTheme();

    const statusKey = status?.toLowerCase();
    const statusConfig = STATUS_CONFIG[statusKey] || {};
    const statusCategory = statusConfig.category || 'composing';
    const categoryConfig = CATEGORY_CONFIG[statusCategory];

    const tooltipTitle = getTooltipTitleForStatus(statusKey);
    const CategoryIcon = categoryConfig.icon;
    const categoryColor = categoryConfig.color;
    const categoryLabel = categoryConfig.label;
    const isNewStatus = statusCategory === 'new';
    const textColor = isNewStatus ? theme.palette.text.primary : categoryColor;
    const bgColor = isNewStatus ? categoryColor : 'transparent';
    const borderColor = isNewStatus ? theme.palette.text.primary : categoryColor;

    const omitMinWidthStatuses = [
        'submitted',
        'pending sb approval',
        'pending ob approval',
    ];
    const hasMinWidth = !omitMinWidthStatuses.includes(statusKey);

    return (
        <Tooltip title={tooltipTitle} placement="top" arrow>
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.5 * SIZE_FACTOR,
                    py: 0.5 * SIZE_FACTOR,
                    gap: SIZE_FACTOR,
                    ...(hasMinWidth && {minWidth: `${10 * SIZE_FACTOR}rem`}),
                    borderRadius: 999,
                    fontWeight: 'bold',
                    backgroundColor: bgColor,
                    border: `1px solid ${borderColor}`,
                    color: textColor,
                }}
            >
                <CategoryIcon htmlColor={textColor} sx={CATEGORY_ICON_SX} />
                <Typography
                    variant="body2"
                    sx={{fontSize: `${0.875 * SIZE_FACTOR}rem`}}
                    data-cy={'status-badge-label'}
                >
                    {categoryLabel}
                </Typography>

                {typeof statusConfig.requesterComplete !== 'undefined' && (
                    <Dot
                        fillColor={
                            statusConfig.requesterComplete ?
                                theme.palette.success.main :
                                theme.palette.error.main
                        }
                    >
                        R
                    </Dot>
                )}
                {typeof statusConfig.spenderComplete !== 'undefined' && (
                    <Dot
                        fillColor={
                            statusConfig.spenderComplete ?
                                theme.palette.success.main :
                                theme.palette.error.main
                        }
                    >
                        {statusConfig.spenderLabel || 'S'}
                    </Dot>
                )}
            </Box>
        </Tooltip>
    );
}

StatusBadge.propTypes = {
    status: PropTypes.oneOf(Object.keys(STATUSES_TO_STRING_VALUE)).isRequired,
};
